import { useState } from 'react';
import ProfileImageChange from '@components/common/profileImageChange';
import { keys as accResultKeys } from '@components/common/header/messageModal/messages/companyMessage/resultRequest/query/useAccResultsProgressComplete';
import { keys as paccResultKeys } from '@components/common/header/messageModal/messages/companyMessage/resultRequest/query/usePHSPACCResultsProgressComplete';
import { keys as pstResultKeys } from '@components/common/header/messageModal/messages/companyMessage/resultRequest/query/usePHSPSTProgressComplete';
import { ProfileInfoType } from '@components/match/profile/types';
import JobSelectNeeded from '@components/match/profile/profileCard/jobSelectNeeded';
import MyProfile from '@components/match/profile/profileCard/todoList/myProfile';
import SkillReport from '@components/match/profile/profileCard/todoList/skillReport';
import JobGroupTitlesSelectModal from '@components/match/modalsWrap/jobGroupTitlesSelectModal';
import { useAccResultSelectMatching } from '@repository/acc';
import { usePHSPACCResultSelectMatching } from '@components/phs/pacc/results/query/useResults';
import TermsModal from '@components/profile/modals/terms';
import { useProfileAgreeInformation } from '@repository/terms/useTerms';
import { keys as matchProfileKeys } from '@components/match/query/useProfilesType';
import { usePHSPSTResultSelectMatching } from '@repository/phs/pst/usePhsPst';
import MatchPreparationStatus from './matchPreparationStatus';
import MatchResultSelectModal from './matchResultSelectModal';
import MatchingProfileVO from '@domain/vo/match/MatchingProfileVO';
import { AccProgressCompleteVO } from '@domain/vo/acca/test/AccProgressCompleteListVO';
import { PaccProgressCompleteVO } from '@domain/vo/phs/pacc/results/PaccProgressCompleteListVO';
import { PstProgressCompleteVO } from '@domain/vo/phs/pst/result/PstProgressCompleteListVO';
import { ApplyType } from '@domain/rs/match/MatchingProfileRs';
import { queryClient } from '@utils/queryClient';
import useModals from '@utils/hooks/useModals';

import classnames from 'classnames/bind';
import styles from './profileCard.module.scss';
const cx = classnames.bind(styles);

interface IProfileCardProps {
  name: string;
  profileInfoType: ProfileInfoType;
  isJobSelectNeeded: boolean;
  matchProfile: MatchingProfileVO;
  selectedResultSn: number | null;
  setSelectedResultSn: React.Dispatch<React.SetStateAction<number | null>>;
  resultsData: AccProgressCompleteVO[] | PaccProgressCompleteVO[] | PstProgressCompleteVO[] | null;
  currentApplyType: ApplyType | null;
  setCurrentApplyType: React.Dispatch<React.SetStateAction<ApplyType | null>>;
  onSuccessJobSelect: () => void;
  matchingConfigYn?: boolean;
}

const ProfileCard = ({
  name,
  profileInfoType,
  isJobSelectNeeded,
  matchProfile,
  selectedResultSn,
  setSelectedResultSn,
  resultsData,
  currentApplyType,
  setCurrentApplyType,
  onSuccessJobSelect,
  matchingConfigYn,
}: IProfileCardProps) => {
  const [profileInfoTypeWithJobSelectModal, setProfileInfoTypeWithJobSelectModal] = useState<ProfileInfoType>();
  const { closeModal, openModal, isVisible } = useModals(['JOB_SELECT', 'RESULT_SELECT', 'TERMS'] as const);

  const { refetch: refetchAgreeInformation } = useProfileAgreeInformation(true);

  const refetchMatchProfileAndResult = (keys: string[]) => {
    // 결과표 선택 후 프로필 / 결과표 재조회
    queryClient.invalidateQueries('matchProfile');
    queryClient.invalidateQueries(keys);
  };

  const { mutate: selectAccResult } = useAccResultSelectMatching(() => {
    refetchMatchProfileAndResult(accResultKeys);
  });
  const { mutate: selectPaccResult } = usePHSPACCResultSelectMatching(() => {
    refetchMatchProfileAndResult(paccResultKeys);
  });
  const { mutate: selectPstResult } = usePHSPSTResultSelectMatching(() => {
    refetchMatchProfileAndResult(pstResultKeys);
  });

  const handleClickSkillReport = (applyType: ApplyType) => {
    setCurrentApplyType(applyType);
    openModal('RESULT_SELECT');
  };

  const handleJobSelectModalOpen = async () => {
    const { data } = await refetchAgreeInformation();
    if (data?.privacyPolicyYn) {
      setProfileInfoTypeWithJobSelectModal(profileInfoType);
      openModal('JOB_SELECT');
    } else {
      openModal('TERMS');
    }
  };

  const handleResultSelectModalClose = () => {
    setSelectedResultSn(null);
    setCurrentApplyType(null);
    closeModal();
  };

  const handleResultSelectModalConfirm = () => {
    if (!selectedResultSn || !currentApplyType) return;

    switch (currentApplyType) {
      case 'ACC':
        selectAccResult(selectedResultSn);
        break;
      case 'PACC':
        selectPaccResult(selectedResultSn);
        break;
      case 'PST':
        selectPstResult(selectedResultSn);
        break;
    }
    handleResultSelectModalClose();
  };

  const handleJobSelectModalClose = () => {
    closeModal();
    onSuccessJobSelect();
    queryClient.invalidateQueries(matchProfileKeys);
  };

  return (
    <section className={cx('container', { isJobSelectNeeded })}>
      {isJobSelectNeeded ? (
        <>
          {/* 직군/직무 선택 전 */}
          <JobSelectNeeded profileInfoType={profileInfoType} onClick={handleJobSelectModalOpen} />
          {/* 직군/직무 선택 모달 */}
          <JobGroupTitlesSelectModal
            visible={isVisible('JOB_SELECT')}
            onClose={closeModal}
            onSuccess={handleJobSelectModalClose}
            profileInfoType={profileInfoTypeWithJobSelectModal}
          />
          {/* 동의 모달 */}
          <TermsModal visible={isVisible('TERMS')} onClose={closeModal} onConfirm={handleJobSelectModalOpen} />
        </>
      ) : (
        <>
          {/* 프로필 변경 */}
          <ProfileImageChange />
          <div className={cx('name')}>{name}님</div>
          {/* 매칭 준비중 / 매칭 준비완료 */}
          <MatchPreparationStatus
            profileInfoType={profileInfoType}
            matchProfile={matchProfile}
            matchingConfigYn={matchingConfigYn}
          />
          {/* 내 프로필 작성 유무 */}
          <MyProfile {...matchProfile.profile} profileInfoType={profileInfoType} />
          {/* 역검/개역검/개구검 결과표 선택 유무 */}
          <SkillReport
            profileInfoType={profileInfoType}
            applyResults={matchProfile.applyResults}
            onClick={handleClickSkillReport}
          />
          {/* 역검/개역검/개구검 결과표 선택 모달 */}
          <MatchResultSelectModal
            selectedResultSn={selectedResultSn}
            setSelectedResultSn={setSelectedResultSn}
            isVisible={isVisible('RESULT_SELECT')}
            onClose={handleResultSelectModalClose}
            onConfirm={handleResultSelectModalConfirm}
            applyType={currentApplyType}
            resultsData={resultsData}
          />
        </>
      )}
    </section>
  );
};

export default ProfileCard;
